import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { getLabelTranslations } from '../api/languageApi';

const LanguageContext = createContext(null);

// Used until the visitor picks a language (not persisted, so pickers still
// treat it as an unconfirmed default).
export const DEFAULT_LANGUAGE = { code: 'en', name: 'English', nativeName: 'English' };

const LANGUAGE_KEY = 'language';
const TRANSLATIONS_KEY = 'translations';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeJson = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
};

const readTranslations = () => {
  const stored = readJson(TRANSLATIONS_KEY);
  return stored && typeof stored === 'object' ? stored : {};
};

// Labels are stored as { agency: { label_key: { label_value } } } and looked up
// with an 'agency.label_key' key, e.g. t('agency.contact_us_btn', 'Contact Us').
// `params` fills {placeholders} in the value: t('agency.x_msg', 'Hi {name}', { name }).
function lookup(translations, key, fallback, params) {
  let value = translations[key];
  if (value == null) {
    const dot = key.indexOf('.');
    value = dot > 0 ? translations[key.slice(0, dot)]?.[key.slice(dot + 1)] : undefined;
  }
  if (value && typeof value === 'object') value = value.label_value;
  let text = typeof value === 'string' && value !== '' ? value : (fallback ?? key);
  if (params) text = text.replace(/\{(\w+)\}/g, (m, name) => (params[name] != null ? params[name] : m));
  return text;
}

// Non-hook lookup for code outside React components (module-level code,
// plain callbacks). Reads the stored labels directly, so it reflects the
// latest fetched translations but does not trigger re-renders.
export const translate = (key, fallback, params) => lookup(readTranslations(), key, fallback, params);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => readJson(LANGUAGE_KEY) || DEFAULT_LANGUAGE);
  const [translations, setTranslationsState] = useState(readTranslations);

  // Fetches the labels for a language and stores them in localStorage.
  // Throws on failure; previously stored language/labels stay untouched.
  const loadTranslations = useCallback(async (code) => {
    const labels = await getLabelTranslations(code);
    writeJson(TRANSLATIONS_KEY, labels);
    setTranslationsState(labels);
    return labels;
  }, []);

  // Select (or switch) language: fetch fresh labels first, then persist the
  // choice, so a failed fetch never leaves a language without its labels.
  const changeLanguage = useCallback(async (next) => {
    await loadTranslations(next.code);
    writeJson(LANGUAGE_KEY, next);
    setLanguageState(next);
  }, [loadTranslations]);

  // Refresh labels for the saved language on app start (picks up admin edits).
  useEffect(() => {
    const code = (readJson(LANGUAGE_KEY) || DEFAULT_LANGUAGE).code;
    if (code) loadTranslations(code).catch((err) => console.warn('Label refresh failed', err));
  }, [loadTranslations]);

  // Keep the <html lang> attribute and other tabs in sync.
  useEffect(() => {
    if (language?.code) document.documentElement.lang = language.code;
  }, [language]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== null && e.key !== LANGUAGE_KEY) return;
      setLanguageState(readJson(LANGUAGE_KEY) || DEFAULT_LANGUAGE);
      setTranslationsState(readTranslations());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Falls back to the provided default (or the key) so labels never render blank.
  const t = useCallback((key, fallback, params) => lookup(translations, key, fallback, params), [translations]);

  const value = useMemo(() => ({ language, changeLanguage, t }), [language, changeLanguage, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

// `const { t } = useTranslation()` — re-renders when the language changes.
export const useTranslation = () => useContext(LanguageContext);
